/**
 * SailsPage — MADE-303
 *
 * Three-tier sail range for offshore-sails.com.
 * Content is original — rewritten from first principles for Offshore Sails.
 * "Doyle" does not appear on this page.
 */

import React from 'react';
import {
  Box, Button, Chip, Container, Divider, Grid, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Check as CheckIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { MarketingLayout } from './MarketingLayout';

// ── Tier data ────────────────────────────────────────────────────────────────

const TIERS = [
  {
    id: 'offshore',
    name: 'Offshore',
    tagline: 'Our flagship, built for offshore passages',
    warranty: '5 years / 50,000 nm',
    warrantyDetail: 'Workmanship and materials, no exceptions',
    price: 'Premium',
    highlight: true,
    description: [
      'The Offshore sail is built without compromise. It is our answer to the demanding reality of extended blue-water passages: sustained loads, relentless UV, and the expectation that the sail will still perform five years and fifty thousand nautical miles from now.',
      'Every stitch in an Offshore sail is sealed with our Harris Paints UV-protection sealant, developed specifically for sustained tropical UV exposure. Double-layer edge tapes run the full perimeter, and box-style corner reinforcing patches distribute load across the largest possible area at each high-stress point.',
      'A Sunbrella acrylic sunshield protects the cloth when the sail is furled, and a rope luff pad cushions the tack against chafe. This is the only sail range in the industry backed by a full five-year, 50,000-nautical-mile warranty covering both workmanship and materials.',
    ],
  },
  {
    id: 'coastal-plus',
    name: 'Coastal Plus',
    tagline: 'High quality for regular coastal sailing',
    warranty: '2 years / 20,000 nm',
    warrantyDetail: 'Workmanship and materials',
    price: 'Mid-range',
    highlight: false,
    description: [
      'The Coastal Plus is built to the same standard of Dacron cloth quality and finishing care as our Offshore tier, with construction choices that match the typical coastal cruising profile: strong, well-protected, and designed to look good and sail well season after season.',
      'Single-layer edge tapes and radial-corner reinforcing patches are correctly sized for the load levels of coastal use. Our Harris Paints UV-protection sealant and a Sunbrella sunshield are both included, along with the rope luff pad.',
      'Two-year workmanship and materials warranty, up to 20,000 nautical miles.',
    ],
  },
  {
    id: 'coastal',
    name: 'Coastal',
    tagline: 'Solid Dacron quality at an accessible price',
    warranty: '1 year / 10,000 nm',
    warrantyDetail: 'Workmanship and materials',
    price: 'Entry',
    highlight: false,
    description: [
      'The Coastal sail is our entry-level offering. Entry-level at Offshore Sails still means genuine quality Dacron, cut and assembled in our Barbados loft with the same attention that goes into every sail we make.',
      'Single edge tapes, standard corner reinforcing patches, and our Harris Paints UV-protection sealant. A sound, reliable working sail for the recreational sailor who wants quality they can trust.',
      'One-year workmanship and materials warranty, up to 10,000 nautical miles.',
    ],
  },
];

// ── Comparison table rows ─────────────────────────────────────────────────────

const COMPARISON_ROWS: { feature: string; offshore: boolean | string; coastalPlus: boolean | string; coastal: boolean | string }[] = [
  { feature: 'Warranty',                          offshore: '5 yr / 50,000 nm',  coastalPlus: '2 yr / 20,000 nm', coastal: '1 yr / 10,000 nm' },
  { feature: 'Warranty covers labour & materials', offshore: true,                coastalPlus: true,                coastal: true },
  { feature: 'Harris Paints UV-protection sealant',       offshore: true,                coastalPlus: true,                coastal: true },
  { feature: 'Edge tapes',                         offshore: 'Double-layer',      coastalPlus: 'Single',            coastal: 'Single' },
  { feature: 'Corner reinforcing patches',         offshore: 'Box-style',         coastalPlus: 'Radial',            coastal: 'Standard' },
  { feature: 'Sunbrella acrylic sunshield',        offshore: true,                coastalPlus: true,                coastal: false },
  { feature: 'Rope luff pad',                      offshore: true,                coastalPlus: true,                coastal: false },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function CellValue({ value }: { value: boolean | string }) {
  if (value === true) return <CheckIcon sx={{ color: '#002680', fontSize: 20 }} />;
  if (value === false) return <RemoveIcon sx={{ color: '#bbb', fontSize: 20 }} />;
  return <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>{value}</Typography>;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const SailsPage: React.FC = () => {
  return (
    <MarketingLayout pageTitle="Our Sail Range">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#002680', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h3" sx={{ color: 'white', fontWeight: 700, mb: 1.5 }}>
            Our Sail Range
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 540, mx: 'auto', lineHeight: 1.75 }}>
            Three tiers of hand-built Dacron sails, each designed for a different
            sailing profile, all built in our Barbados loft.
          </Typography>
          <Box sx={{ width: 60, height: 4, bgcolor: '#FFC726', mx: 'auto', mt: 3 }} />
        </Container>
      </Box>

      {/* ── Tier cards ─────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 }, bgcolor: '#fafbff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="stretch">
            {TIERS.map(tier => (
              <Grid key={tier.id} item xs={12} md={4}>
                <Box
                  data-testid={`tier-${tier.id}`}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: 'white',
                    borderRadius: 3,
                    border: tier.highlight ? '2px solid #FFC726' : '1px solid rgba(0,38,128,0.10)',
                    boxShadow: tier.highlight
                      ? '0 8px 32px rgba(0,38,128,0.12)'
                      : '0 2px 12px rgba(0,38,128,0.05)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Tier header */}
                  <Box
                    sx={{
                      bgcolor: tier.highlight ? '#002680' : '#f5f7fb',
                      px: 3,
                      py: 3,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: tier.highlight ? 'white' : '#002680',
                        }}
                      >
                        {tier.name}
                      </Typography>
                      {tier.highlight && (
                        <Chip
                          label="Flagship"
                          size="small"
                          sx={{ bgcolor: '#FFC726', color: '#002680', fontWeight: 700, fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{ color: tier.highlight ? 'rgba(255,255,255,0.75)' : '#666' }}
                    >
                      {tier.tagline}
                    </Typography>
                  </Box>

                  {/* Warranty badge */}
                  <Box sx={{ px: 3, py: 2, bgcolor: tier.highlight ? '#001a60' : '#eef1fb' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: tier.highlight ? 'rgba(255,255,255,0.6)' : '#666',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: '0.65rem',
                        display: 'block',
                        mb: 0.25,
                      }}
                    >
                      Warranty
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 700, color: tier.highlight ? '#FFC726' : '#002680' }}
                    >
                      {tier.warranty}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: tier.highlight ? 'rgba(255,255,255,0.55)' : '#888' }}
                    >
                      {tier.warrantyDetail}
                    </Typography>
                  </Box>

                  {/* Body text */}
                  <Box sx={{ px: 3, py: 3, flex: 1 }}>
                    {tier.description.map((para, i) => (
                      <Typography
                        key={i}
                        variant="body2"
                        sx={{ color: '#555', lineHeight: 1.8, mb: i < tier.description.length - 1 ? 2 : 0 }}
                      >
                        {para}
                      </Typography>
                    ))}
                  </Box>

                  {/* CTA */}
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Button
                      component={RouterLink}
                      to="/os/contact"
                      fullWidth
                      variant={tier.highlight ? 'contained' : 'outlined'}
                      sx={
                        tier.highlight
                          ? {
                              bgcolor: '#FFC726',
                              color: '#002680',
                              fontWeight: 700,
                              textTransform: 'none',
                              '&:hover': { bgcolor: '#e6a800' },
                            }
                          : {
                              borderColor: '#002680',
                              color: '#002680',
                              fontWeight: 700,
                              textTransform: 'none',
                              '&:hover': { bgcolor: 'rgba(0,38,128,0.05)' },
                            }
                      }
                    >
                      Enquire about {tier.name}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Comparison table ───────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" sx={{ color: '#002680', fontWeight: 700, mb: 1.5, textAlign: 'center' }}>
            Feature Comparison
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', mb: 5, maxWidth: 520, mx: 'auto' }}>
            Every tier is built in our Barbados loft with the same Dacron cloth and
            the same craftsmanship. The differences lie in construction specification.
          </Typography>

          <TableContainer
            data-testid="sails-comparison-table"
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(0,38,128,0.10)',
              boxShadow: '0 2px 12px rgba(0,38,128,0.05)',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#002680' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', width: '34%' }}>
                    Feature
                  </TableCell>
                  <TableCell align="center" sx={{ color: '#FFC726', fontWeight: 700, fontSize: '0.95rem' }}>
                    Offshore
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                    Coastal Plus
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: '0.95rem' }}>
                    Coastal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {COMPARISON_ROWS.map(({ feature, offshore, coastalPlus, coastal }, i) => (
                  <TableRow
                    key={feature}
                    sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafbff' }}
                  >
                    <TableCell sx={{ color: '#333', fontWeight: 500 }}>{feature}</TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(255,199,38,0.05)' }}>
                      <CellValue value={offshore} />
                    </TableCell>
                    <TableCell align="center">
                      <CellValue value={coastalPlus} />
                    </TableCell>
                    <TableCell align="center">
                      <CellValue value={coastal} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>

      {/* ── Warranty call-out ──────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fafbff', py: { xs: 6, md: 8 }, borderTop: '1px solid rgba(0,38,128,0.08)' }}>
        <Container maxWidth="md">
          <Box
            sx={{
              bgcolor: '#002680',
              borderRadius: 3,
              p: { xs: 4, md: 5 },
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" sx={{ color: '#FFC726', fontWeight: 700, mb: 1.5 }}>
              The Only Warranty of Its Kind
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', maxWidth: 560, mx: 'auto', lineHeight: 1.8, mb: 3 }}>
              We are one of the only companies in the industry that warrants both
              workmanship and materials on our flagship Offshore sails for five full
              years or fifty thousand nautical miles. That is not a figure chosen for
              marketing impact. It reflects what we know our construction can deliver.
            </Typography>
            <Button
              component={RouterLink}
              to="/os/contact"
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#FFC726',
                color: '#002680',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: '#e6a800' },
              }}
            >
              Request a Quote
            </Button>
          </Box>
        </Container>
      </Box>

      <Divider />

    </MarketingLayout>
  );
};
